import numeral from 'numeral'
import { dataTypes, numberFormats } from '@adalo/constants'
import { getUserLocale } from './user-locale'

import { formatCoordinate } from './coordinates'
import { formatDate, formatDateOnly } from './luxon/formatters'

const ID_FIELD_ID = 'id'

const currencySymbols = {
  usd: '$',
  eur: '€',
  gbp: '£',
  jpy: '¥',
  cny: '¥',
  aud: '$',
  cad: '$',
  nzd: '$',
  chf: 'CHF',
  sek: 'kr',
  nok: 'kr',
  dkk: 'kr',
  inr: '₹',
  krw: '₩',
  brl: 'R$',
  mxn: '$',
  sgd: '$',
  hkd: 'HK$',
  pln: 'zł',
  czk: 'Kč',
  rub: '₽',
  zar: 'R',
  try: '₺',
  ils: '₪',
}

export const formatValue = (binding, value, format = {}, helpers = {}) => {
  if (!binding || !binding.source) {
    return ''
  }

  const isID = binding.source.fieldId === ID_FIELD_ID

  if (isID) {
    format = {
      ...format,
      type: numberFormats.NONE,
    }
  }

  let result = value

  if (value === undefined || value === null || !String(value)) {
    if (
      binding &&
      binding.source &&
      binding.source.dataType === dataTypes.IMAGE
    ) {
      return value
    }

    return ''
  }

  if (helpers.isUploadURL) {
    const { parentSource } = helpers

    const baseURL =
      parentSource?.dataType === dataTypes.IMAGE
        ? helpers.getImageUploadsBaseURL && helpers.getImageUploadsBaseURL()
        : helpers.getFileUploadsBaseURL && helpers.getFileUploadsBaseURL()

    if (baseURL) {
      return `${baseURL}/${value}?orient`
    }
  }

  switch (binding.source.dataType) {
    case dataTypes.DATE:
      result = formatDate(value, format)
      break
    case dataTypes.DATE_ONLY:
      result = formatDateOnly(value, format)
      break
    case dataTypes.NUMBER:
      result = formatNumber(value, format)
      break
  }

  const parentSource = binding && binding.source && binding.source.source

  if (parentSource && parentSource.dataType === dataTypes.LOCATION) {
    const [dataSubType, field] = binding.source.fieldId.split('.')

    if (dataSubType === 'coordinates') {
      result = formatCoordinate(value, field, format)
    }
  }

  let { prefix, suffix } = format

  if (binding.source.dataType === dataTypes.NUMBER && +value === 1) {
    prefix = format.prefixSingular
    suffix = format.suffixSingular
  }

  if (prefix) {
    result = `${prefix} ${result}`
  }
  if (suffix) {
    result = `${result} ${suffix}`
  }

  return result
}

export const formatNumber = (num, format) => {
  const { type } = format
  const locale = getUserLocale()

  if (Array.isArray(num)) {
    const results = num.map(itm => formatNumber(itm, format))

    if (results.length === 1) {
      return results[0]
    } else {
      return results.join(' - ')
    }
  }

  switch (type) {
    case numberFormats.COMMAS:
      return numeral(num).format('0,0[.][0][0]')
    case numberFormats.ABBREVIATED:
      return numeral(num).format(abbreviatedFormat(num)).toUpperCase()
    case numberFormats.CURRENCY:
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: format.currency || 'usd',
      }).format(num)
    case numberFormats.ABBREVIATED_CURRENCY: {
      const currencySymbol = getCurrencySymbol(format.currency)
      const formattedNumber = numeral(num)
        .format(abbreviatedFormat(num))
        .toUpperCase()

      return `${currencySymbol}${formattedNumber}`
    }
    default:
      return Number(num)
  }
}

export const getCurrencySymbol = currencyCode => {
  const currency = (currencyCode || 'usd').toLowerCase()
  return currencySymbols[currency] || '$'
}

export const abbreviatedFormat = num => {
  const order = ((3 * Math.log(num)) / Math.log(1000)) % 3
  let format = '0[.][00]a'

  if (order >= 2) {
    format = '0a'
  } else if (order >= 1) {
    format = '0[.][0]a'
  }

  return format
}

export const isURL = value => {
  return !!value && typeof value === 'string' && value.startsWith('https://')
}
